import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCallback } from 'react';
import type { NodeMouseHandler } from '@xyflow/react';
import type { LayoutResult } from './layout-treemap';
import {
  getFirstChildTreemapFocusId,
  getParentTreemapFocusId,
  getSiblingTreemapFocusId,
} from './treemap-navigation';
import {
  runTreemapKeyDown,
  setHoveredNodeIfPresent,
} from './treemap-interaction-helpers';
import type { SetHoveredNodeId } from './treemap-interaction-types';
import type { ParsedTreemap, TreemapFlowNode } from './treemap-types';
import type { TreemapViewportRect } from './treemap-view-model';

interface UseTreemapFocusControlsInput {
  ascendDrill: () => void;
  drillIntoNode: (nodeId: string) => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  hoveredNodeId: string | null;
  layout: LayoutResult | null;
  parentMap: Map<string, string>;
  parsed: ParsedTreemap | null;
  rectById: Map<string, TreemapViewportRect>;
  setHoveredNodeId: SetHoveredNodeId;
}

interface TreemapFocusControls {
  handleKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  handlePaneClick: () => void;
  onNodeMouseEnter: NodeMouseHandler<TreemapFlowNode>;
  onNodeMouseLeave: NodeMouseHandler<TreemapFlowNode>;
}

export function useTreemapFocusControls({
  ascendDrill,
  drillIntoNode,
  handleZoomIn,
  handleZoomOut,
  hoveredNodeId,
  layout,
  parentMap,
  parsed,
  rectById,
  setHoveredNodeId,
}: UseTreemapFocusControlsInput): TreemapFocusControls {
  const focusNode = useCallback(
    (nodeId: string | null): void => {
      setHoveredNodeIfPresent({ nodeId, setHoveredNodeId });
    },
    [setHoveredNodeId],
  );

  const focusPreviousSibling = useCallback((): void => {
    focusNode(
      getSiblingTreemapFocusId({
        parsed,
        layout,
        hoveredNodeId,
        parentMap,
        rectById,
        offset: -1,
      }),
    );
  }, [focusNode, parsed, layout, hoveredNodeId, parentMap, rectById]);

  const focusNextSibling = useCallback((): void => {
    focusNode(
      getSiblingTreemapFocusId({
        parsed,
        layout,
        hoveredNodeId,
        parentMap,
        rectById,
        offset: 1,
      }),
    );
  }, [focusNode, parsed, layout, hoveredNodeId, parentMap, rectById]);

  const focusParent = useCallback((): void => {
    focusNode(getParentTreemapFocusId({ parsed, layout, hoveredNodeId, parentMap, rectById }));
  }, [focusNode, parsed, layout, hoveredNodeId, parentMap, rectById]);

  const focusFirstChild = useCallback((): void => {
    focusNode(getFirstChildTreemapFocusId({ parsed, layout, hoveredNodeId, rectById }));
  }, [focusNode, parsed, layout, hoveredNodeId, rectById]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>): void => {
      runTreemapKeyDown({
        event,
        layout,
        hoveredNodeId,
        handleZoomIn,
        handleZoomOut,
        ascendDrill,
        focusParent,
        focusFirstChild,
        focusPreviousSibling,
        focusNextSibling,
        drillIntoNode,
      });
    },
    [
      layout,
      hoveredNodeId,
      handleZoomIn,
      handleZoomOut,
      ascendDrill,
      focusParent,
      focusFirstChild,
      focusPreviousSibling,
      focusNextSibling,
      drillIntoNode,
    ],
  );

  const onNodeMouseEnter: NodeMouseHandler<TreemapFlowNode> = useCallback(
    (_event, node): void => setHoveredNodeId(node.id),
    [setHoveredNodeId],
  );

  const onNodeMouseLeave: NodeMouseHandler<TreemapFlowNode> = useCallback(
    (): void => setHoveredNodeId(null),
    [setHoveredNodeId],
  );

  const handlePaneClick = useCallback((): void => {
    setHoveredNodeId(null);
  }, [setHoveredNodeId]);

  return {
    handleKeyDown,
    handlePaneClick,
    onNodeMouseEnter,
    onNodeMouseLeave,
  };
}
